import { useLocation, useNavigate } from "react-router-dom";
import { chatApi } from "../api/client";
import { useShell, useViewport } from "../lib/shell";
import { useToast } from "../lib/toast";
import { encodeModelSelection, loadLastModelSelection } from "../lib/chatModel";
import "./MobileTopBar.css";

/**
 * The single fixed chrome row on phones and tablets — the pattern every
 * mobile chat app converges on (ChatGPT, Claude, Perplexity): drawer
 * handle on the left, current context in the middle, "new chat" on the
 * right. It replaces the free-floating hamburger that used to sit *on top
 * of* each view's own header and cover its title.
 *
 * Rendered only when the measured device is not a desktop (see
 * `lib/shell.tsx`) — on desktop the permanent sidebar carries all of this.
 */
const ROUTE_TITLES: Array<[string, string]> = [
  ["/search", "Search"],
  ["/projects", "Projects"],
  ["/memory", "Memory"],
  ["/research", "Deep research"],
  ["/images", "Images"],
  ["/skills", "Skills"],
  ["/plugins", "Plugins"],
  ["/mcp", "MCP Servers"],
  ["/settings", "Settings"],
];

function routeTitle(pathname: string): string {
  return ROUTE_TITLES.find(([prefix]) => pathname.startsWith(prefix))?.[1] ?? "chatter";
}

export default function MobileTopBar() {
  const { toggleDrawer, drawerOpen, pageTitle } = useShell();
  const { isDesktop } = useViewport();
  const navigate = useNavigate();
  const location = useLocation();
  const toast = useToast();

  if (isDesktop) return null;

  const newChat = async () => {
    try {
      const lastModel = loadLastModelSelection();
      const c = await chatApi.create({
        title: "New Chat",
        model_override: lastModel ? encodeModelSelection(lastModel) : undefined,
      });
      navigate(`/chat/${c.id}`);
    } catch (e) {
      toast.error("Failed to create chat: " + (e as Error).message);
    }
  };

  const onChatRoute = location.pathname === "/" || location.pathname.startsWith("/chat");
  // Only the chat route has a title worth carrying (the conversation's own).
  // Every other route names itself, so the bar reads the route rather than
  // whatever title the last view happened to leave behind.
  const title = onChatRoute ? pageTitle : routeTitle(location.pathname);

  return (
    <header className="mobile-topbar">
      <button
        className="mobile-topbar-btn"
        type="button"
        aria-label={drawerOpen ? "Close menu" : "Open menu"}
        aria-expanded={drawerOpen}
        onClick={toggleDrawer}
      >
        <span aria-hidden="true">☰</span>
      </button>
      <span className="mobile-topbar-title" title={title}>
        {title}
      </span>
      {onChatRoute ? (
        <button
          className="mobile-topbar-btn"
          type="button"
          aria-label="New chat"
          onClick={() => void newChat()}
        >
          <span aria-hidden="true">✎</span>
        </button>
      ) : (
        <span className="mobile-topbar-btn mobile-topbar-spacer" aria-hidden="true" />
      )}
    </header>
  );
}
